import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  CardActions,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Chip,
  Divider,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ViewModule as ShelfIcon,
  LibraryBooks as LibraryIcon,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';

import { rayonnagesAPI } from '../services/api';

function RayonnageCard({ rayonnage, onEdit, onDelete }) {
  const navigate = useNavigate();

  const handleViewBooks = () => {
    if (rayonnage.id) {
      // Naviguer vers la liste des livres avec le filtre rayonnage
      navigate(`/livres?rayonnage_id=${rayonnage.id}`);
    }
  };

  return (
    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <CardContent sx={{ flexGrow: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <ShelfIcon sx={{ mr: 1, color: 'primary.main' }} />
          <Typography variant="h6" component="h3" noWrap>
            {rayonnage.nom}
          </Typography>
        </Box>
        
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <LibraryIcon sx={{ mr: 1, fontSize: 16, color: 'text.secondary' }} />
          <Typography variant="body2" color="text.secondary">
            {rayonnage.bibliotheque_nom}
            {rayonnage.bibliotheque_piece && ` (${rayonnage.bibliotheque_piece})`}
          </Typography>
        </Box>
        
        {rayonnage.description && (
          <Typography variant="body2" sx={{ mb: 2 }}>
            {rayonnage.description}
          </Typography>
        )}
        
        <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
          <Chip
            label={`${rayonnage.nombre_livres} livre${rayonnage.nombre_livres > 1 ? 's' : ''}`}
            size="small"
            color="secondary"
            variant="outlined"
            onClick={handleViewBooks}
            sx={{
              cursor: 'pointer',
              '&:hover': {
                backgroundColor: 'secondary.light',
              }
            }}
          />
          {(rayonnage.position_x !== 0 || rayonnage.position_y !== 0) && (
            <Chip
              label={`Position: ${rayonnage.position_x}, ${rayonnage.position_y}`}
              size="small"
              color="default"
              variant="outlined"
            />
          )}
        </Box>
      </CardContent>
      
      <Divider />
      <CardActions>
        <IconButton size="small" onClick={() => onEdit(rayonnage)}>
          <EditIcon />
        </IconButton>
        <IconButton size="small" color="error" onClick={() => onDelete(rayonnage)}>
          <DeleteIcon />
        </IconButton>
      </CardActions>
    </Card>
  );
}

function RayonnagesPage() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [deleteDialog, setDeleteDialog] = useState({ open: false, rayonnage: null });

  // Requête pour récupérer les rayonnages
  const { data, isLoading, error } = useQuery(
    'rayonnages',
    () => rayonnagesAPI.getAll().then(res => res.data.data),
    {
      staleTime: 2 * 60 * 1000,
    }
  );

  // Mutation pour supprimer un rayonnage
  const deleteRayonnageMutation = useMutation(
    (id) => rayonnagesAPI.delete(id),
    {
      onSuccess: () => {
        toast.success('Rayonnage supprimé avec succès');
        queryClient.invalidateQueries('rayonnages');
        queryClient.invalidateQueries('rayonnages-list');
        setDeleteDialog({ open: false, rayonnage: null });
      },
      onError: (error) => {
        if (error.response?.status === 409) {
          toast.error('Impossible de supprimer ce rayonnage car il contient des livres');
        } else {
          toast.error('Erreur lors de la suppression');
        }
      }
    }
  );

  const handleAdd = () => {
    navigate('/rayonnages/ajouter');
  };

  const handleEdit = (rayonnage) => {
    navigate(`/rayonnages/${rayonnage.id}/modifier`);
  };

  const handleDelete = (rayonnage) => {
    setDeleteDialog({ open: true, rayonnage });
  };

  const confirmDelete = () => {
    if (deleteDialog.rayonnage) {
      deleteRayonnageMutation.mutate(deleteDialog.rayonnage.id);
    }
  };


  if (error) {
    return (
      <Alert severity="error">
        Erreur lors du chargement des rayonnages: {error.message}
      </Alert>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Gestion des rayonnages
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAdd}
        >
          Ajouter un rayonnage
        </Button>
      </Box>

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Grid container spacing={3}>
          {data?.map((rayonnage) => (
            <Grid item xs={12} sm={6} md={4} key={rayonnage.id}>
              <RayonnageCard
                rayonnage={rayonnage}
                onEdit={handleEdit}
                onDelete={handleDelete}
              />
            </Grid>
          ))}
        </Grid>
      )}

      <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false, rayonnage: null })}>
        <DialogTitle>Confirmer la suppression</DialogTitle>
        <DialogContent>
          <Typography>
            Êtes-vous sûr de vouloir supprimer le rayonnage "{deleteDialog.rayonnage?.nom}" ?
            Cette action est irréversible.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, rayonnage: null })}>
            Annuler
          </Button>
          <Button 
            onClick={confirmDelete} 
            color="error" 
            variant="contained"
            disabled={deleteRayonnageMutation.isLoading}
          >
            {deleteRayonnageMutation.isLoading ? (
              <CircularProgress size={20} />
            ) : (
              'Supprimer'
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default RayonnagesPage;
